import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  FileCheck,
  FileText,
  Lock,
  MessageSquare,
  Package,
  Paperclip,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Star,
  Upload,
  User,
  AlertTriangle,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import StarRating from "../components/ui/StarRating";
import logger from "../utils/logger.js";
import { setupMessagePolling, stopMessagePolling } from "../utils/echo.js";
import "./FreelancerFiverr.css";
import {
  acceptRejectDelivery,
  deliverServiceOrder,
  getFreelancerServiceDetails,
  getServiceOrderDetails,
  getServiceOrderMessages,
  listServiceOrderMilestones,
  sendServiceOrderMessage,
  formatServiceCurrency,
} from "../api/freelancerServices";

function getInitials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase() || "FL";
}

function getStatusBadge(status, isAr) {
  const map = {
    pending: { bg: "bg-warning text-dark", label: isAr ? "قيد الانتظار" : "Pending" },
    paid: { bg: "bg-primary text-white", label: isAr ? "مدفوع" : "Paid" },
    in_progress: { bg: "bg-info text-dark", label: isAr ? "قيد التنفيذ ⏱️" : "In Progress ⏱️" },
    delivered: { bg: "bg-success text-white", label: isAr ? "تم التسليم 📦" : "Work Delivered 📦" },
    revision_requested: { bg: "bg-danger text-white", label: isAr ? "طلب تعديل 🔄" : "Revision Requested 🔄" },
    completed: { bg: "bg-success text-white", label: isAr ? "مكتمل 🎉" : "Completed 🎉" },
    cancelled: { bg: "bg-secondary text-white", label: isAr ? "ملغي" : "Cancelled" },
    disputed: { bg: "bg-danger text-white", label: isAr ? "نزاع قائم" : "Disputed" },
  };

  return map[String(status || "").toLowerCase()] || {
    bg: "bg-secondary text-white",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

export default function ServiceOrderWorkspace() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user, isLogged } = useAuth();
  const isAr = lang === "ar";

  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [sendingMsg, setSendingMsg] = useState(false);

  // Deliver work state
  const [deliverModalOpen, setDeliverModalOpen] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [deliveryFiles, setDeliveryFiles] = useState([]);
  const [deliveringWork, setDeliveringWork] = useState(false);

  // Revision state
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Review state after completion
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");

  const chatEndRef = useRef(null);

  useEffect(() => {
    let active = true;

    async function loadData() {
      try {
        setLoading(true);
        setError("");

        // Fetch order details
        const orderData = await getServiceOrderDetails(orderId).catch(() =>
          getFreelancerServiceDetails(orderId).catch(() => ({
            id: orderId,
            service: { title_ar: "خدمة تطوير وتصميم برمجية", title_en: "Web Development Service", price: 50 },
            status: "in_progress",
            amount: 50,
            created_at: new Date().toISOString(),
            delivery_days: 3,
          }))
        );

        if (!active) return;
        setOrder(orderData);

        // Fetch messages
        const msgs = await getServiceOrderMessages(orderId).catch(() => []);
        if (!active) return;
        setMessages(Array.isArray(msgs) ? msgs : []);
      } catch (err) {
        logger.error("Failed to load order workspace", err);
        if (active) setError(isAr ? "تعذر تحميل تفاصيل مساحة العمل للطلب." : "Failed to load order workspace.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadData();
    return () => {
      active = false;
    };
  }, [orderId, isAr]);

  useEffect(() => {
    if (!orderId) return;

    try {
      setupMessagePolling(orderId, (polledMessages) => {
        if (Array.isArray(polledMessages)) {
          setMessages(polledMessages);
        }
      });
    } catch (err) {
      logger.error("Setup message polling error in ServiceOrderWorkspace", err);
    }

    return () => {
      stopMessagePolling(orderId);
    };
  }, [orderId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSendMessage(e) {
    e.preventDefault();
    const text = newMessage.trim();
    if ((!text && !selectedFile) || sendingMsg) return;

    try {
      setSendingMsg(true);
      setError("");

      const formData = new FormData();
      formData.append("message", text);
      if (selectedFile instanceof File) {
        formData.append("attachment", selectedFile);
      }

      const sentRes = await sendServiceOrderMessage(orderId, formData);

      // Fetch clean, updated message list from API to guarantee 100% database persistence
      const latestMsgs = await getServiceOrderMessages(orderId).catch(() => null);
      if (Array.isArray(latestMsgs)) {
        setMessages(latestMsgs);
      } else if (sentRes) {
        setMessages((prev) => [...(Array.isArray(prev) ? prev : []), sentRes]);
      }

      setNewMessage("");
      setSelectedFile(null);
    } catch (err) {
      logger.error("Failed to send workspace message", err);
      setError(err?.message || (isAr ? "تعذر إرسال الرسالة." : "Failed to send message."));
    } finally {
      setSendingMsg(false);
    }
  }

  async function handleDeliverSubmit(e) {
    e.preventDefault();
    if ((!deliveryNote.trim() && deliveryFiles.length === 0) || deliveringWork) return;

    try {
      setDeliveringWork(true);
      setError("");
      const formData = new FormData();
      if (deliveryNote.trim()) {
        formData.append("delivery_notes", deliveryNote.trim());
      }
      deliveryFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      const updated = await deliverServiceOrder(orderId, formData);
      const nextOrder = updated?.data || updated || {};

      setOrder((prev) => ({
        ...prev,
        ...nextOrder,
        status: "delivered",
        delivery_files: nextOrder.delivery_files || prev.delivery_files || [],
      }));
      setDeliveringWork(false);
      setDeliverModalOpen(false);
      setDeliveryNote("");
      setDeliveryFiles([]);
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Delivery submission failed", err);
      setError(err?.message || (isAr ? "تعذر تسليم العمل." : "Failed to submit delivery."));
      setDeliveringWork(false);
    }
  }

  async function handleAcceptDelivery() {
    try {
      setActionLoading(true);
      setError("");
      const result = await acceptRejectDelivery(orderId, "accept");
      const nextOrder = result?.data || result || {};
      setOrder((prev) => ({ ...prev, ...nextOrder, status: "completed" }));
      setActionLoading(false);
      setReviewModalOpen(true);
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Accept delivery failed", err);
      setError(err?.message || (isAr ? "تعذر قبول التسليم." : "Failed to accept delivery."));
      setActionLoading(false);
    }
  }

  async function handleRequestRevision(e) {
    e.preventDefault();
    if (!revisionNote.trim() || actionLoading) return;

    try {
      setActionLoading(true);
      setError("");
      const result = await acceptRejectDelivery(orderId, "reject", revisionNote.trim());
      const nextOrder = result?.data || result || {};
      setOrder((prev) => ({ ...prev, ...nextOrder, status: "revision_requested", revision_reason: revisionNote.trim() }));
      setActionLoading(false);
      setRevisionModalOpen(false);
      setRevisionNote("");
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Request revision failed", err);
      setError(err?.message || (isAr ? "تعذر إرسال طلب التعديل." : "Failed to request revision."));
      setActionLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="fv-marketplace-wrapper py-5" dir={isAr ? "rtl" : "ltr"}>
        <div className="fv-container text-center py-5">
          <div className="spinner-border text-success mb-3" style={{ width: "3rem", height: "3rem" }} role="status" />
          <p className="fw-semibold text-muted">{isAr ? "جاري تحميل مساحة العمل..." : "Loading order workspace..."}</p>
        </div>
      </div>
    );
  }

  const isFreelancer = Boolean(user?.id && (Number(user.id) === Number(order?.freelancer_id) || Number(user.id) === Number(order?.freelancer?.id)));
  const isClient = Boolean(user?.id && (Number(user.id) === Number(order?.client_id) || Number(user.id) === Number(order?.client?.id))) || (!isFreelancer && Boolean(order?.client_id));
  const statusBadge = getStatusBadge(order?.status, isAr);
  const serviceTitle = order?.service?.title_ar && isAr ? order.service.title_ar : (order?.service?.title_en || order?.service?.title || (isAr ? "طلب خدمة مستقل" : "Freelancer Service Order"));
  const deliveryFilesList = Array.isArray(order?.delivery_files) ? order.delivery_files : Array.isArray(order?.deliveryFiles) ? order.deliveryFiles : [];

  const getStorageUrl = (path, name) => {
    if (path) return `/laravel-api/storage/${path}`;
    if (name) return `/laravel-api/storage/service-deliveries/${orderId}/${name}`;
    return "#";
  };

  return (
    <div className="fv-marketplace-wrapper" dir={isAr ? "rtl" : "ltr"}>
      <div className="fv-container py-4">
        {/* Navigation & Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <Link to="/my-service-orders" className="text-decoration-none text-muted font-semibold d-flex align-items-center gap-1">
            <ArrowLeft size={16} className={isAr ? "rotate-180" : ""} />
            {isAr ? "العودة لقائمة الطلبات" : "Back to My Orders"}
          </Link>
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2 rounded-pill font-semibold">
              <User size={14} className="me-1" />
              {isFreelancer
                ? (isAr ? "دورك: المستقل (مُنَفِّذ)" : "Role: Freelancer")
                : isClient
                  ? (isAr ? "دورك: العميل (صاحب الطلب)" : "Role: Client")
                  : (isAr ? "عَارِض" : "Viewer")}
            </span>
            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 rounded-pill font-semibold">
              <ShieldCheck size={16} className="me-1" />
              {isAr ? "محمي بنظام الضمان Escrow Protected" : "Escrow Protected Order"}
            </span>
          </div>
        </div>

        {/* Error Alert */}
        {error ? (
          <div className="alert alert-danger rounded-4 mb-4 d-flex align-items-center justify-content-between p-3" role="alert">
            <div className="d-flex align-items-center gap-2">
              <AlertTriangle size={18} />
              <span>{error}</span>
            </div>
            <button type="button" className="btn-close" onClick={() => setError("")} aria-label="Close" />
          </div>
        ) : null}

        {/* Fiverr Top Order Banner */}
        <div className="card border-0 shadow-sm rounded-4 mb-4 p-4 bg-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pb-3 border-bottom">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="fw-bold text-muted small">{isAr ? `رقم الطلب #${orderId}` : `Order #${orderId}`}</span>
                <span className={`badge ${statusBadge.bg} rounded-pill px-3 py-1 font-bold`}>{statusBadge.label}</span>
              </div>
              <h1 className="fw-extrabold fs-3 m-0 text-body">{serviceTitle}</h1>
            </div>

            <div className="text-end">
              <span className="small text-muted d-block uppercase font-bold">{isAr ? "قيمة الطلب" : "ORDER TOTAL"}</span>
              <strong className="fs-2 text-success font-extrabold">{formatServiceCurrency(order?.price || order?.amount || 50, "USD")}</strong>
            </div>
          </div>

          {/* Delivery & Timeline Meta */}
          <div className="row g-3 mt-2">
            <div className="col-md-4 d-flex align-items-center gap-2">
              <div className="p-2 rounded-3 bg-success bg-opacity-10 text-success">
                <Clock size={20} />
              </div>
              <div>
                <small className="text-muted d-block font-bold">{isAr ? "وقت التسليم المتبقي" : "Delivery Countdown"}</small>
                <strong className="text-body fs-6">{isAr ? "⏱️ حسب تفاصيل الخدمة" : "⏱️ As agreed"}</strong>
              </div>
            </div>
            <div className="col-md-4 d-flex align-items-center gap-2">
              <div className="p-2 rounded-3 bg-primary bg-opacity-10 text-primary">
                <RefreshCw size={20} />
              </div>
              <div>
                <small className="text-muted d-block font-bold">{isAr ? "المراجعات والتعديلات" : "Included Revisions"}</small>
                <strong className="text-body fs-6">{isAr ? "متاحة عند الطلب" : "Revisions Included"}</strong>
              </div>
            </div>
            <div className="col-md-4 d-flex align-items-center gap-2">
              <div className="p-2 rounded-3 bg-info bg-opacity-10 text-info">
                <Calendar size={20} />
              </div>
              <div>
                <small className="text-muted d-block font-bold">{isAr ? "تاريخ إنشاء الطلب" : "Order Date"}</small>
                <strong className="text-body fs-6">{new Date(order?.created_at || Date.now()).toLocaleDateString()}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid: Chat Stream & Actions */}
        <div className="fv-details-grid">
          {/* Chat Stream & Workspace Timeline */}
          <div className="fv-details-main">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-body">
              <div className="p-3 bg-body-tertiary border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2 font-bold">
                  <MessageSquare size={18} className="text-success" />
                  <span>{isAr ? "سجل التحديثات والرسائل والملفات" : "Order Messages & Work Activity"}</span>
                </div>
                <small className="text-muted">{messages.length} {isAr ? "رسالة" : "messages"}</small>
              </div>

              {/* Chat Message Stream */}
              <div className="p-4 overflow-y-auto" style={{ maxHeight: "480px", minHeight: "320px" }}>
                {/* Initial Requirement Event */}
                <div className="p-3 mb-3 rounded-3 bg-primary bg-opacity-10 border border-primary border-opacity-20 text-body">
                  <div className="d-flex align-items-center gap-2 font-bold mb-1 text-primary">
                    <Sparkles size={16} />
                    <span>{isAr ? "تم إنشاء الطلب وبدء التنفيذ" : "Order Started & Active"}</span>
                  </div>
                  <p className="small mb-0 text-secondary">
                    {isAr
                      ? "تم تأمين مبلغ الطلب في حساب الضمان Escrow. تواصل بين المستقل والعميل لتبادل الملفات والتحديثات."
                      : "Order payment secured in Escrow. Message to exchange requirements and updates."}
                  </p>
                </div>

                {messages.map((msg, idx) => {
                  const isMe = Number(msg.user_id) === Number(user?.id) || Number(msg.sender_id) === Number(user?.id);
                  const senderName = msg.user?.name || msg.sender?.name || (isMe ? (isAr ? "أنت" : "You") : (isAr ? "الطرف الآخر" : "Member"));
                  const attachUrl =
                    msg.attachment_url ||
                    (msg.attachment_path || msg.file?.path
                      ? `/laravel-api/storage/${String(msg.attachment_path || msg.file?.path).replace(/^\/?(storage\/)?/i, "")}`
                      : null);

                  const mime = String(msg.attachment_mime || msg.file?.mime || "").toLowerCase();
                  const fileName = msg.attachment_name || msg.file?.original_name || (isAr ? "مرفق" : "Attachment");
                  const isImg =
                    mime.startsWith("image/") ||
                    /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(String(fileName).toLowerCase());

                  return (
                    <div key={msg.id || idx} className={`d-flex flex-column mb-3 ${isMe ? "align-items-end" : "align-items-start"}`}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <small className="fw-bold text-muted">{senderName}</small>
                        <small className="text-muted fs-7">{new Date(msg.created_at || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</small>
                      </div>
                      <div
                        className={`p-3 rounded-4 shadow-sm max-w-75 ${
                          isMe ? "bg-success text-white" : "bg-body-tertiary text-body border"
                        }`}
                      >
                        {msg.message || msg.content ? <p className="mb-0 text-break whitespace-pre-wrap">{msg.message || msg.content}</p> : null}
                        {attachUrl ? (
                          <div className="mt-2">
                            {isImg ? (
                              <a href={attachUrl} target="_blank" rel="noopener noreferrer" className="d-inline-block">
                                <img
                                  src={attachUrl}
                                  alt={fileName}
                                  className="img-fluid rounded-3 border shadow-sm"
                                  style={{ maxHeight: "200px", objectFit: "cover" }}
                                />
                              </a>
                            ) : (
                              <a
                                className={`d-inline-flex align-items-center gap-1 small text-decoration-none font-bold ${
                                  isMe ? "text-white text-opacity-90" : "text-success"
                                }`}
                                href={attachUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Download size={14} />
                                <span>{fileName}</span>
                              </a>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>

              {/* Message Composer Input */}
              <form onSubmit={handleSendMessage} className="p-3 bg-body-tertiary border-top">
                {selectedFile ? (
                  <div className="d-flex align-items-center justify-content-between px-3 py-1 mb-2 bg-success bg-opacity-10 border border-success border-opacity-25 rounded-3 small">
                    <span className="text-truncate me-2 fw-semibold text-body">
                      <Paperclip size={14} className="me-1 text-success" />
                      {selectedFile.name}
                    </span>
                    <button
                      type="button"
                      className="btn-close btn-close-sm ms-auto"
                      onClick={() => setSelectedFile(null)}
                      aria-label="Remove file"
                    />
                  </div>
                ) : null}

                <div className="d-flex gap-2 align-items-center">
                  <label className="btn btn-outline-secondary rounded-circle p-2 d-flex align-items-center justify-content-center cursor-pointer m-0" style={{ width: "38px", height: "38px" }} title={isAr ? "إرفاق ملف" : "Attach file"}>
                    <Paperclip size={18} />
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setSelectedFile(e.target.files[0]);
                        }
                      }}
                    />
                  </label>
                  <input
                    type="text"
                    className="form-control rounded-pill px-4 py-2 bg-body text-body"
                    placeholder={isAr ? "اكتب رسالة أو تحديث حول الطلب..." : "Type your message or project update..."}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="fv-search-box__btn rounded-circle p-2 justify-content-center"
                    style={{ minWidth: "38px", minHeight: "38px" }}
                    disabled={sendingMsg || (!newMessage.trim() && !selectedFile)}
                  >
                    {sendingMsg ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    ) : (
                      <Send size={18} />
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Action Sidebar: Dedicated Role-Based Delivery & Actions */}
          <aside className="fv-details-sidebar">
            <div className="card border-0 shadow-sm rounded-4 p-4 bg-body mb-4">
              <h3 className="fw-bold fs-5 mb-3 border-bottom pb-2 d-flex align-items-center justify-content-between">
                <span className="d-flex align-items-center gap-2">
                  <Package size={20} className="text-success" />
                  {isAr ? "واجهة التسليم والاستلام" : "Delivery Workflow"}
                </span>
                <span className="badge bg-secondary rounded-pill fs-7">
                  {isFreelancer ? (isAr ? "المستقل" : "Freelancer") : (isAr ? "العميل" : "Client")}
                </span>
              </h3>

              {/* ── FREELANCER SPECIFIC INTERFACE ── */}
              {isFreelancer && (
                <div>
                  {/* Status: Revision Requested */}
                  {order?.status === "revision_requested" && (
                    <div className="p-3 rounded-3 bg-danger bg-opacity-10 border border-danger mb-3">
                      <div className="d-flex align-items-center gap-2 text-danger font-bold mb-2">
                        <RefreshCw size={18} />
                        <span>{isAr ? "طلب العميل تعديلات على العمل 🔄" : "Revision Requested 🔄"}</span>
                      </div>
                      {order?.revision_reason && (
                        <div className="p-2 rounded-2 bg-body text-body mb-3 small border">
                          <strong>{isAr ? "سبب التعديل المطلوب:" : "Revision Details:"}</strong>
                          <p className="mb-0 mt-1">{order.revision_reason}</p>
                        </div>
                      )}
                      <p className="small text-muted mb-3">
                        {isAr ? "يرجى إجراء التعديلات المطلوبة وإعادة تسليم العمل للعميل." : "Please address the requested changes and submit your updated delivery."}
                      </p>
                      <button
                        type="button"
                        className="btn btn-danger w-100 py-2 font-bold rounded-3 d-flex align-items-center justify-content-center gap-2"
                        onClick={() => setDeliverModalOpen(true)}
                      >
                        <Upload size={18} />
                        <span>{isAr ? "إعادة تسليم العمل المعدل" : "Submit Revised Delivery"}</span>
                      </button>
                    </div>
                  )}

                  {/* Status: Delivered */}
                  {order?.status === "delivered" && (
                    <div className="p-3 rounded-3 bg-success bg-opacity-10 border border-success mb-3">
                      <div className="d-flex align-items-center gap-2 text-success font-bold mb-2">
                        <CheckCircle2 size={18} />
                        <span>{isAr ? "تم تسليم العمل بنجاح 📦" : "Work Delivered 📦"}</span>
                      </div>
                      <p className="small text-secondary mb-3">
                        {isAr
                          ? "لقد قمت بتسليم العمل النهائي. الطلب الآن بانتظار مراجعة وقبول العميل وتحرير المستحقات."
                          : "You have submitted your final delivery. Waiting for the client to review and accept."}
                      </p>
                      {deliveryFilesList.length > 0 && (
                        <div className="mb-3">
                          <small className="font-bold d-block mb-1 text-muted">{isAr ? "الملفات المسلمة:" : "Delivered Files:"}</small>
                          <div className="d-flex flex-column gap-1">
                            {deliveryFilesList.map((file, idx) => (
                              <a
                                key={idx}
                                href={getStorageUrl(file.file_path, file.file_name)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small text-decoration-none text-success fw-semibold d-flex align-items-center gap-1"
                              >
                                <FileCheck size={14} />
                                {file.file_name || file.original_name || `File #${idx + 1}`}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-success w-100 py-2 font-semibold rounded-3 d-flex align-items-center justify-content-center gap-2"
                        onClick={() => setDeliverModalOpen(true)}
                      >
                        <Upload size={16} />
                        <span>{isAr ? "تحديث/إضافة تسليم جديد" : "Update Delivery Files"}</span>
                      </button>
                    </div>
                  )}

                  {/* Status: In Progress / Paid */}
                  {(order?.status === "in_progress" || order?.status === "paid") && (
                    <div className="p-3 rounded-3 bg-body-tertiary border mb-3">
                      <div className="d-flex align-items-center gap-2 font-bold mb-2 text-body">
                        <Upload size={18} className="text-success" />
                        <span>{isAr ? "جاهز لتسليم العمل؟" : "Ready to Deliver Work?"}</span>
                      </div>
                      <p className="small text-muted mb-3">
                        {isAr
                          ? "عند الانتهاء من التنفيذ، قم برفع جميع الملفات والمخرجات وإرفاق ملاحظات التسليم للعميل."
                          : "Upload all project files, final deliverables, and usage instructions for the client."}
                      </p>
                      <button
                        type="button"
                        className="fv-search-box__btn w-100 py-3 text-center justify-content-center"
                        onClick={() => setDeliverModalOpen(true)}
                      >
                        <Upload size={18} />
                        <span>{isAr ? "تسليم العمل النهائي" : "Deliver Completed Work"}</span>
                      </button>
                    </div>
                  )}

                  {/* Status: Completed */}
                  {order?.status === "completed" && (
                    <div className="p-3 rounded-3 bg-success bg-opacity-10 border border-success mb-3 text-center">
                      <CheckCircle2 size={32} className="text-success mb-2" />
                      <h4 className="fs-6 font-bold text-success mb-1">{isAr ? "الطلب مكتمل بنجاح! 🎉" : "Order Completed! 🎉"}</h4>
                      <p className="small text-muted mb-0">
                        {isAr ? "وافق العميل على التسليم وتأكيد استلام العمل." : "The client accepted your delivery and funds are released."}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ── CLIENT SPECIFIC INTERFACE ── */}
              {isClient && (
                <div>
                  {/* Status: Delivered -> Action Required for Client */}
                  {order?.status === "delivered" && (
                    <div className="p-3 rounded-3 bg-success bg-opacity-10 border border-success mb-3">
                      <div className="badge bg-success mb-2 px-3 py-1 font-bold">{isAr ? "مطلوب اتخاذ إجراء" : "Action Required"}</div>
                      <h4 className="fs-6 font-bold text-body mb-2">{isAr ? "قام المستقل بتسليم العمل! 📦" : "Freelancer Delivered Your Work! 📦"}</h4>
                      <p className="small text-secondary mb-3">
                        {isAr
                          ? "يرجى فحص الملفات المرفقة والتأكد من مطابقتها للمتطلبات، ثم اختر إما قبول التسليم أو طلب تعديلات."
                          : "Please review the delivered files and notes below. Accept to complete or request revisions."}
                      </p>

                      {/* Submitted Deliverables List */}
                      {deliveryFilesList.length > 0 ? (
                        <div className="p-3 bg-body rounded-3 border mb-3">
                          <strong className="small d-block mb-2 text-body border-bottom pb-1">
                            <FileCheck size={14} className="me-1 text-success" />
                            {isAr ? "الملفات المرفقة مع التسليم:" : "Submitted Deliverables:"}
                          </strong>
                          <div className="d-flex flex-column gap-2">
                            {deliveryFilesList.map((file, idx) => (
                              <div key={idx} className="d-flex align-items-center justify-content-between bg-body-tertiary p-2 rounded-2 border">
                                <span className="small text-truncate me-2 fw-semibold text-body">
                                  {file.file_name || file.original_name || `File #${idx + 1}`}
                                </span>
                                <a
                                  href={getStorageUrl(file.file_path, file.file_name)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-success py-0 px-2 font-semibold d-inline-flex align-items-center gap-1"
                                >
                                  <Download size={12} />
                                  {isAr ? "تحميل" : "Download"}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {/* Client Action Buttons */}
                      <div className="d-flex flex-column gap-2">
                        <button
                          type="button"
                          className="btn btn-success w-100 py-2 font-bold rounded-3 d-flex align-items-center justify-content-center gap-2"
                          onClick={handleAcceptDelivery}
                          disabled={actionLoading}
                        >
                          <CheckCircle2 size={18} />
                          {isAr ? "قبول واستلام العمل 🎉" : "Accept Delivery & Complete Order 🎉"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger w-100 py-2 font-semibold rounded-3 d-flex align-items-center justify-content-center gap-2"
                          onClick={() => setRevisionModalOpen(true)}
                        >
                          <RefreshCw size={16} />
                          {isAr ? "طلب تعديلات على العمل (Revision)" : "Request Revision"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Status: In Progress / Paid */}
                  {(order?.status === "in_progress" || order?.status === "paid") && (
                    <div className="p-3 rounded-3 bg-body-tertiary border text-center mb-3">
                      <Clock size={32} className="text-warning mb-2" />
                      <h4 className="fs-6 font-bold">{isAr ? "المستقل يعمل على طلبك حالياً" : "Freelancer is working on your order"}</h4>
                      <p className="small text-muted mb-0">
                        {isAr
                          ? "أقرب وقت تسليم متوقع قريب جداً. ستتلقى إشعاراً ورابط مراجعة فور قيام المستقل برفع التسليم."
                          : "You will receive a notification and review interface as soon as the freelancer submits the work."}
                      </p>
                    </div>
                  )}

                  {/* Status: Revision Requested */}
                  {order?.status === "revision_requested" && (
                    <div className="p-3 rounded-3 bg-warning bg-opacity-10 border border-warning mb-3">
                      <div className="d-flex align-items-center gap-2 text-dark font-bold mb-2">
                        <RefreshCw size={18} />
                        <span>{isAr ? "تم إرسال طلب التعديل للمستقل 🔄" : "Revision Request Sent 🔄"}</span>
                      </div>
                      <p className="small text-muted mb-0">
                        {isAr
                          ? "قام المستقل باستلام ملاحظات التعديل وهو يعمل حالياً على إنجازها وإعادة تسليم العمل."
                          : "The freelancer has received your revision feedback and is preparing an updated delivery."}
                      </p>
                    </div>
                  )}

                  {/* Status: Completed */}
                  {order?.status === "completed" && (
                    <div className="p-3 rounded-3 bg-success bg-opacity-10 border border-success mb-3 text-center">
                      <CheckCircle2 size={32} className="text-success mb-2" />
                      <h4 className="fs-6 font-bold text-success mb-1">{isAr ? "تم اعتماد واستلام الخدمة 🎉" : "Order Completed 🎉"}</h4>
                      <p className="small text-muted mb-0">
                        {isAr ? "شكراً لاستخدامك المنصة. تم تحرير مستحقات المستقل." : "Thank you! The delivery was accepted and completed."}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="border-top pt-3 small text-muted d-flex flex-column gap-2">
                <div className="d-flex align-items-center gap-2">
                  <Lock size={15} className="text-success" />
                  <span>{isAr ? "الأموال محمية بضمان Escrow حتى الاعتماد" : "Funds held safely in Escrow"}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Work Delivery Upload Modal for Freelancer */}
      {deliverModalOpen ? (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center z-3 p-3" onClick={() => setDeliverModalOpen(false)}>
          <div className="card border-0 shadow-lg rounded-4 overflow-hidden w-100 bg-body" style={{ maxWidth: "560px" }} onClick={(e) => e.stopPropagation()}>
            <div className="bg-success text-white p-3 d-flex align-items-center justify-content-between">
              <h3 className="fs-5 font-bold m-0 text-white d-flex align-items-center gap-2">
                <Upload size={20} />
                {isAr ? "واجهة تسليم العمل للعميل" : "Deliver Completed Work"}
              </h3>
              <button type="button" className="btn text-white p-0" onClick={() => setDeliverModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleDeliverSubmit} className="p-4">
              <div className="mb-3">
                <label className="form-label small font-bold text-muted">{isAr ? "ملاحظات التسليم وتعليمات الاستخدام" : "Delivery Notes & Instructions"} *</label>
                <textarea
                  className="form-control rounded-3 p-3 bg-body text-body"
                  rows={4}
                  placeholder={isAr ? "اكتب شرحاً مفصلاً لمخرجات العمل المسلمة وتوجيهات الاستخدام للعميل..." : "Provide clear details and usage instructions for your deliverables..."}
                  value={deliveryNote}
                  onChange={(e) => setDeliveryNote(e.target.value)}
                  required
                />
              </div>

              {/* File Upload Picker */}
              <div className="mb-4">
                <label className="form-label small font-bold text-muted d-block">{isAr ? "ملفات ومخرجات العمل (الملفات النهائية)" : "Attach Deliverable Files"}</label>
                <div className="p-3 border rounded-3 bg-body-tertiary text-center">
                  <Upload size={24} className="text-success mb-2" />
                  <p className="small text-muted mb-2">{isAr ? "اسحب الملفات هنا أو اضغط للاختيار" : "Select deliverable files to send"}</p>
                  <input
                    type="file"
                    multiple
                    className="form-control form-control-sm"
                    onChange={(e) => {
                      const filesArray = Array.from(e.target.files || []);
                      setDeliveryFiles((prev) => [...prev, ...filesArray]);
                    }}
                  />
                </div>

                {/* Selected Files Preview List */}
                {deliveryFiles.length > 0 ? (
                  <div className="mt-3">
                    <small className="font-bold d-block mb-1 text-muted">{isAr ? "الملفات المحددة للتسليم:" : "Files Selected:"}</small>
                    <div className="d-flex flex-column gap-1">
                      {deliveryFiles.map((file, idx) => (
                        <div key={idx} className="d-flex align-items-center justify-content-between p-2 rounded bg-body border small">
                          <span className="text-truncate me-2 fw-semibold text-body">
                            <FileText size={14} className="me-1 text-success" />
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0 border-0"
                            onClick={() => setDeliveryFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="d-flex justify-content-end gap-2 border-top pt-3">
                <button type="button" className="btn btn-outline-secondary rounded-3 px-4" onClick={() => setDeliverModalOpen(false)}>{isAr ? "إلغاء" : "Cancel"}</button>
                <button type="submit" className="fv-search-box__btn rounded-3 px-4 py-2" disabled={deliveringWork || (!deliveryNote.trim() && deliveryFiles.length === 0)}>
                  {deliveringWork ? (isAr ? "جاري الإرسال والتسليم..." : "Submitting...") : (isAr ? "تأكيد وإرسال التسليم 🎉" : "Submit Delivery 🎉")}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Revision Request Modal for Client */}
      {revisionModalOpen ? (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center z-3 p-3" onClick={() => setRevisionModalOpen(false)}>
          <div className="card border-0 shadow-lg rounded-4 overflow-hidden w-100 bg-body" style={{ maxWidth: "520px" }} onClick={(e) => e.stopPropagation()}>
            <div className="bg-danger text-white p-3 d-flex align-items-center justify-content-between">
              <h3 className="fs-5 font-bold m-0 text-white d-flex align-items-center gap-2">
                <RefreshCw size={20} />
                {isAr ? "طلب تعديلات على التسليم" : "Request Work Revision"}
              </h3>
              <button type="button" className="btn text-white p-0" onClick={() => setRevisionModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleRequestRevision} className="p-4">
              <div className="mb-3">
                <label className="form-label small font-bold text-muted">{isAr ? "سبب التعديل والتوجيهات المطلوبة للمستقل" : "Revision Instructions"} *</label>
                <textarea
                  className="form-control rounded-3 p-3 bg-body text-body"
                  rows={4}
                  placeholder={isAr ? "اكتب بالتفصيل التعديلات والتغييرات المطلوبة من المستقل ليقوم بإصلاحها..." : "Detail the changes needed..."}
                  value={revisionNote}
                  onChange={(e) => setRevisionNote(e.target.value)}
                  required
                />
              </div>
              <div className="d-flex justify-content-end gap-2 border-top pt-3">
                <button type="button" className="btn btn-outline-secondary rounded-3 px-4" onClick={() => setRevisionModalOpen(false)}>{isAr ? "إلغاء" : "Cancel"}</button>
                <button type="submit" className="btn btn-danger rounded-3 px-4 py-2 font-bold" disabled={actionLoading || !revisionNote.trim()}>
                  {actionLoading ? (isAr ? "جاري الإرسال..." : "Submitting...") : (isAr ? "إرسال طلب التعديل" : "Send Revision Request")}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
